export type StateUpdateFn<T> = (context: T, dt: number) => void;
export type StateTransitionFn<T> = (context: T) => boolean;

export interface State<T> {
  name: string;
  onEnter?: (context: T) => void;
  onUpdate?: StateUpdateFn<T>;
  onExit?: (context: T) => void;
}

export interface Transition<T> {
  from: string;
  to: string;
  condition: StateTransitionFn<T>;
}

export class StateMachine<T> {
  private states: Map<string, State<T>> = new Map();
  private transitions: Transition<T>[] = [];
  private currentState: State<T> | null = null;
  private context: T;

  constructor(context: T) {
    this.context = context;
  }

  public addState(state: State<T>): this {
    this.states.set(state.name, state);
    return this;
  }

  public addTransition(from: string, to: string, condition: StateTransitionFn<T>): this {
    this.transitions.push({ from, to, condition });
    return this;
  }

  public setState(stateName: string): void {
    const newState = this.states.get(stateName);
    if (!newState) {
      console.warn(`State "${stateName}" not found`);
      return;
    }

    if (this.currentState?.onExit) {
      this.currentState.onExit(this.context);
    }

    this.currentState = newState;

    if (this.currentState.onEnter) {
      this.currentState.onEnter(this.context);
    }
  }

  public update(dt: number): void {
    if (!this.currentState) return;

    // Check transitions
    for (const transition of this.transitions) {
      if (transition.from === this.currentState.name && transition.condition(this.context)) {
        this.setState(transition.to);
        break;
      }
    }

    // Update current state
    if (this.currentState.onUpdate) {
      this.currentState.onUpdate(this.context, dt);
    }
  }

  public getCurrentStateName(): string | null {
    return this.currentState?.name ?? null;
  }
}
